import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { I } from '../components/Icons'
import { StarWait } from '../components/StarWait'
import { DoneMark } from '../components/DoneMark'
import { SignSheet } from '../components/SignSheet'
import { cancelSale, saleStatus } from '../lib/api'
import { useLiveRows, pollEvery } from '../lib/live'
import { fmt2, type Employee } from '../lib/types'
import type { SaleDoneState } from './SellReview'

// Opening Shopify POS from a web page: iOS only lets a page run a Shortcut, and the Shortcut opens the app. The Shortcuts
// app flashes for a moment on the way; that is iOS, not something the page can skip. Shopify POS has no URL scheme of its own
// (trying one makes Safari say "the address is invalid"). One-time setup per iPad: Shortcuts > + > Open App > Shopify POS, named "Open Shopify POS".
const OPEN_POS_SHORTCUT = 'shortcuts://run-shortcut?name=' + encodeURIComponent('Open Shopify POS')
export function openPos() { window.location.href = OPEN_POS_SHORTCUT }

export function SellCard() {
  const nav = useNav()
  const s = useLocation().state as SaleDoneState | null
  const [status, setStatus] = useState<{ status: string; shopify_order_name: string | null; total_cents: number } | null>(null)
  const [cancel, setCancel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => { if (!s) nav('/sell', { replace: true }) }, [s, nav])
  // the sale row is heard live (L2); the poll stays as the fallback, slowed while the live link is up
  const tickRef = useRef<() => void>(() => {})
  const live = useLiveRows('sales', s ? `id=eq.${s.sale_id}` : null, () => tickRef.current())
  useEffect(() => {
    if (!s) return
    let on = true
    const tick = async () => { const r = await saleStatus(s.sale_id).catch(() => null); if (on && r) setStatus(r) }
    tickRef.current = tick
    tick(); const t = setInterval(tick, pollEvery(live))
    const vis = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', vis)
    return () => { on = false; clearInterval(t); document.removeEventListener('visibilitychange', vis) }
  }, [s, live])
  if (!s) return null
  const paid = status?.status === 'paid'
  const cancelled = status?.status === 'cancelled'
  const doCancel = async (e: Employee) => {
    setBusy(true); setErr('')
    try { await cancelSale(s.sale_id, e.employee_id); setCancel(false) } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  return (
    <Page bar={<TopBar title="Card payment" caption={`Sale #${s.sale_no}`} />}>
      <div className="done">
        {paid ? <DoneMark kind="paid" /> : cancelled ? <DoneMark kind="cancelled" /> : <StarWait />}
        <div className="done__title">{paid ? 'Paid' : cancelled ? 'Cancelled' : 'Waiting for the card'}</div>
        <div className="done__sub">#{s.sale_no} · {fmt2(status?.total_cents ?? s.total_cents)} with tax · Card · signed by {s.by.name} ({s.by.employee_id}){paid && status?.shopify_order_name ? ` · Shopify order ${status.shopify_order_name}` : ''}</div>
        {!paid && !cancelled && (
          <div className="card done__list">
            <div className="done__item"><span className="dot ok" style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>1</span><span>Open Shopify POS on this iPad</span></div>
            <div className="done__item"><span className="dot ok" style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>2</span><span>Tap the <b>Starchive</b> tile. It fills the cart with these pieces at the prices agreed here</span></div>
            <div className="done__item"><span className="dot ok" style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>3</span><span>Tap <b>Charge</b> and take the card as usual. This screen updates itself when Shopify confirms</span></div>
          </div>
        )}
        {paid && (
          <div className="card done__list done__list--in">
            <div className="done__item"><span className="dot ok">{I.tick(12)}</span><span>Charged through Shopify Payments, order {status?.shopify_order_name}</span></div>
            <div className="done__item"><span className="dot ok">{I.tick(12)}</span><span>Saved to the sales ledger; Shopify took the pieces out of stock</span></div>
          </div>
        )}
        {err && <div className="banner" style={{ background: '#d70015', marginTop: 12 }}>{err}</div>}
        <div className="done__btns">
          {!paid && !cancelled ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setCancel(true)}>Cancel sale</button>
              <button type="button" className="cta cta--center" onClick={openPos}>Open Shopify POS</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={() => nav('/')}>Home</button>
              <button type="button" className="cta cta--center" onClick={() => nav('/sell')}>New sale</button>
            </>
          )}
        </div>
        {!paid && !cancelled && <div className="hint" style={{ maxWidth: 560, textAlign: 'center', marginTop: 14 }}>If the customer walks or the card is declined, tap Cancel sale so the pieces stay available. Nothing is charged until POS says so.</div>}
      </div>
      {cancel && <SignSheet title={`Cancel sale #${s.sale_no}`} onConfirm={doCancel} onClose={() => !busy && setCancel(false)} busy={busy} />}
    </Page>
  )
}
